import React, { useState, useEffect, useContext, useRef } from "react";
import { Modal, Button, Radio, Input, message, Select, DatePicker } from "antd";
import { platformApi } from "../../api";
import { ContextAPI } from "../../ContextAPI";
import moment from "moment";
import { ENDPOINT } from "../../constants";
import { useBranch } from "../../ContextAPI/branch";

const getReceiptModule = (mod) => {
  switch (mod) {
    case "BOOKING_REGISTER":
      return "booking_register";
    case "JOB_INVOICE":
      return "jobInvoice";
    case "COUNTER_SALES":
      return "counter_sales";
    case "JobOrder":
      return "joborder";
    case "CSO":
      return "cso";
    default:
      return mod;
  }
};

const PaymentModal = ({
  visible,
  onClose,
  module,
  moduleId,
  branchId,
  onSuccess,
  jobInvoice,
  data,
}) => {

  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [amount, setAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const activePlutusIdRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const [paymentTimer, setPaymentTimer] = useState(180);
  const timerIntervalRef = useRef(null);
  const [paymentResult, setPaymentResult] = useState(null);

  const [showDenominationModal, setShowDenominationModal] = useState(false);
  const [collectedDenominations, setCollectedDenominations] = useState([]);
  const [changeDenominations, setChangeDenominations] = useState([]);
  const [step, setStep] = useState(1);
  const [hasChangeToReturn, setHasChangeToReturn] = useState(true);

  const [totalPaid, setTotalPaid] = useState(0);
  const [pendingAmount, setPendingAmount] = useState(0);
  const [totalBill, setTotalBill] = useState(0);
  const [partialPayments, setPartialPayments] = useState([]); // [{method, amount}]
  const [bookingId, setBookingId] = useState(null);
  const [allBranchBanks, setAllBranchBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [receiptDate, setReceiptDate] = useState(moment());
  const { loginCredintials } = useContext(ContextAPI);
  const { selectBranch } = useBranch();

  const targetBranchId = branchId || bookingId?.branch?.id || bookingId?.branchId || data?.branchId || data?.branch?.id;
  const targetBranchIds = targetBranchId
    ? [targetBranchId]
    : (selectBranch && selectBranch.length > 0 ? selectBranch.map(b => b.id) : []);

  const branchBanks = targetBranchIds.length > 0
    ? allBranchBanks.filter(b => targetBranchIds.includes(b.branchId))
    : allBranchBanks;

  const getBranchBanks = async () => {
    try {
      const res = await platformApi.post(`/api/company/branches/get`, {
        page: 1,
        size: 50,
        searchString: false
      });
      if (res.data.code === 200) {
        const data = res.data.response.data;
        // Flatten all branch.bankDetails into a single array
        const banks = data.branch.flatMap(branch =>
          (branch.bankDetails || []).map(bank => ({
            branchId: branch.id,
            branchName: branch.name,
            ...bank, // include all bank fields
          }))
        );

        setAllBranchBanks(banks);
      }
    } catch (error) {
      console.error("Error fetching branch banks:", error);
      message.error("Error fetching branch banks");
    }
  };

  // Default denominations
  const defaultDenoms = [
    { denomination: 500, count: 0 },
    { denomination: 200, count: 0 },
    { denomination: 100, count: 0 },
    { denomination: 50, count: 0 },
    { denomination: 20, count: 0 },
    { denomination: 10, count: 0 },
    { denomination: 5, count: 0 },
    { denomination: 2, count: 0 },
    { denomination: 1, count: 0 },
  ];

  useEffect(() => {
    if (visible) {
      getBranchBanks();
    }
  }, [visible]);


  useEffect(() => {
    if (visible) {
      const defaultMethod = loginCredintials.roleAccess.some(
        (acc) =>
          acc.master === "TRANSACTIONMASTER" && acc.subModule === "Cash Payment"
      )
        ? "CASH"
        : loginCredintials.roleAccess.some(
          (acc) =>
            acc.master === "TRANSACTIONMASTER" &&
            acc.subModule === "POS Machine"
        )
          ? "CARD"
          : "-";
      setPaymentMethod(defaultMethod);
      setPaymentId(null);
      setShowDenominationModal(false);
      setLoading(false);
      setPaymentResult(null);
      setCollectedDenominations(JSON.parse(JSON.stringify(defaultDenoms)));
      setChangeDenominations(JSON.parse(JSON.stringify(defaultDenoms)));
      setAmount(0);
      setTotalBill(0);
      setTotalPaid(0);
      setPendingAmount(0);
      setPartialPayments([]);
      setReceiptDate(moment());
    }
  }, [visible, loginCredintials]);

  useEffect(() => {
    if (visible && moduleId && jobInvoice === "true") {
      const fetchJobInvoice = async () => {
        try {
          const res = await platformApi.get(`/api/jobInvoice/${moduleId}`);
          if (res.data.code === 200) {
            const booking = res.data.response.data;
            setBookingId(booking);
            const totalBillFromApi =
              booking?.payments?.length > 0
                ? Number(
                  booking.payments.reduce(
                    (sum, p) => sum + Number(p.billAmount || 0),
                    0
                  )
                )
                : Number(
                  booking.price?.finalAmount ||
                  booking?.price?.onRoadPrice ||
                  booking.vehicle?.price?.[0]?.showroomPrice ||
                  0
                );

            const alreadyPaid = booking.payments
              ? booking.payments?.reduce(
                (sum, p) =>
                  p.status === "SUCCESS"
                    ? sum + (Number(p.collectedAmount) || 0)
                    : sum,
                0
              )
              : 0;
            const pending = Math.max(booking?.totalInvoice - alreadyPaid, 0);
            setTotalBill(pending);
            setTotalPaid(alreadyPaid);
            setPendingAmount(pending);
            setAmount(pending);
            setPartialPayments(
              booking.payments?.map((p) => ({
                method: p.method,
                amount: Number(p.collectedAmount),
              })) || []
            );
          } else {
            message.error("Failed to fetch booking info");
          }
        } catch (err) {
          console.error("Error fetching booking:", err);
          message.error("Error fetching booking info");
        }
      };

      fetchJobInvoice();
    } else if (module == "BOOKING_REGISTER") {
      const fetchBooking = async () => {
        try {
          const res = await platformApi.get(`/api/booking/${moduleId}`);
          if (res.data.code === 200) {
            const booking = res.data.response.data;
            setBookingId(booking);
            let target = Number(booking?.price?.netRecieveables || 0);

            const isFinanceMode = booking?.price?.paymentMode?.toLowerCase() === "finance";
            if (target === 0 && isFinanceMode) {
              target = Number(
                booking?.price?.finalAmount ||
                booking?.price?.onRoadPrice ||
                booking?.vehicle?.price?.[0]?.showroomPrice ||
                0
              );
            }

            const alreadyPaid = booking.payments?.reduce(
              (sum, p) =>
                p.status === "SUCCESS"
                  ? sum + (Number(p.collectedAmount) || 0)
                  : sum,
              0
            );

            const totalRemaining = Math.max(target - alreadyPaid, 0);

            const prefill = totalRemaining;

            setTotalBill(target);
            setTotalPaid(alreadyPaid);
            setPendingAmount(totalRemaining);
            setAmount(prefill);
            setPartialPayments(
              booking.payments?.map((p) => ({
                method: p.method,
                amount: Number(p.collectedAmount),
              })) || []
            );
          } else {
            message.error("Failed to fetch booking info");
          }
        } catch (err) {
          console.error("Error fetching booking:", err);
          message.error("Error fetching booking info");
        }
      };

      fetchBooking();
    } else if (module === "COUNTER_SALES") {
      const fetchCounterSale = async () => {
        try {
          const res = await platformApi.get(`/api/saleSpareInvoice/${data.id}`);
          if (res.data.code === 200) {
            const counterSale = res.data.response.data;
            setBookingId(counterSale);
            const totalBillFromApi = Number(counterSale?.totalInvoice || 0);

            // Normalize payment field: API may return 'payment' (singular) or 'payments' (array)
            const rawPayments = counterSale.payments || 
              (counterSale.payment
                ? (Array.isArray(counterSale.payment) ? counterSale.payment : [counterSale.payment])
                : []);

            const alreadyPaid = rawPayments.reduce(
              (sum, p) =>
                p.status === "SUCCESS"
                  ? sum + (Number(p.collectedAmount) || 0)
                  : sum,
              0
            );

            const pending = Math.max(totalBillFromApi - alreadyPaid, 0);

            setTotalBill(totalBillFromApi);
            setTotalPaid(alreadyPaid);
            setPendingAmount(pending);
            setAmount(pending);
            setPartialPayments(
              rawPayments.map((p) => ({
                method: p.method,
                amount: Number(p.collectedAmount),
              }))
            );
          } else {
            message.error("Failed to fetch counter sale info");
          }
        } catch (err) {
          console.error("Error fetching counter sale:", err);
          message.error("Error fetching counter sale info");
        }
      };

      fetchCounterSale();
    } else if (module === "CSO") {
      // Customer Spare Order integration
      const fetchCustomerSpareOrder = async () => {
        try {
          // Use data prop directly if available, otherwise fetch
          if (data) {
            setBookingId(data);
            const totalBillFromApi = Number(data.GrandTotal || 0);
            const alreadyPaid = Number(data.Advance || 0);
            const pending = Number(data.Balance || 0);

            setTotalBill(totalBillFromApi);
            setTotalPaid(alreadyPaid);
            setPendingAmount(pending);
            setAmount(pending);
          }
        } catch (err) {
          console.error("Error processing customer spare order:", err);
          message.error("Error processing customer spare order");
        }
      };

      fetchCustomerSpareOrder();
    } else if (module === "JobOrder") {
      // Job Order integration
      const fetchJobOrder = async () => {
        try {
          // Use data prop directly if available
          if (data) {
            setBookingId(data);
            const totalBillFromApi = Number(data.totalInvoice || 0);

            // Calculate already paid amount from payments array
            const alreadyPaid = data.payments?.reduce(
              (sum, p) =>
                p.status === "SUCCESS"
                  ? sum + (Number(p.collectedAmount) || 0)
                  : sum,
              0
            ) || 0;

            // Calculate pending amount
            const pending = Math.max(totalBillFromApi - alreadyPaid, 0);

            setTotalBill(totalBillFromApi);
            setTotalPaid(alreadyPaid);
            setPendingAmount(pending);
            setAmount(pending);
            setPartialPayments(
              data.payments?.map((p) => ({
                method: p.method,
                amount: Number(p.collectedAmount),
              })) || []
            );
          }
        } catch (err) {
          console.error("Error processing job order:", err);
          message.error("Error processing job order");
        }
      };

      fetchJobOrder();
    }

  }, [visible, moduleId, module, data]);

  const setEReceipt = async (payload) => {
    // API call removed: Backend now generates the e-receipt ID internally
    return "GENERATED_BY_BACKEND";
  };

  const totalCollected = collectedDenominations.reduce(
    (sum, d) => sum + d.denomination * (parseInt(d.count) || 0),
    0
  );

  const totalChange = changeDenominations.reduce(
    (sum, d) => sum + d.denomination * (parseInt(d.count) || 0),
    0
  );

  const autoChange = totalCollected - amount;

  const createPaymentPending = async () => {
    try {
      const payload = {
        module,
        paidBy: bookingId?.partyName?.id || bookingId?.customer?.id,
        collectedBy: loginCredintials?.id,
        createdBy: loginCredintials?.id,
        moduleId,
        billAmount: totalBill,
        status: "PENDING",
        method: paymentMethod,
        dateOfPayment: receiptDate ? receiptDate.toISOString() : null,
        date: receiptDate ? receiptDate.format("YYYY-MM-DD") : null,
      };
      let eReceiptId = await setEReceipt({ branch: branchId });
      payload.eReceiptId = eReceiptId;
      payload.branch = branchId;

      const response = await platformApi.post("/api/payment/create", payload);
      if (response.data.code === 200) {
        console.log("Payment request created with pending");
        setPaymentId(response.data.response.data.id);
        return response.data.response.data.id;
      } else {
        message.error("Failed to create pending payment");
        return null;
      }
    } catch (err) {
      console.error("Error creating pending payment:", err);
      message.error("Failed to create pending payment");
      return null;
    }
  };

  const updateCSOPaymentStatus = async (isFullyPaid) => {
    try {
      const res = await platformApi.post(
        `/api/customerSpareOrderRouter/payment/${moduleId}`,
        { status: isFullyPaid }
      );
      if (res.data.code === 200) {
        console.log(`CSO payment status updated: ${isFullyPaid ? 'Fully Paid' : 'Partially Paid'}`);
      } else {
        message.warning("Failed to update CSO payment status");
      }
    } catch (err) {
      console.error("Error updating CSO payment status:", err);
      message.error("Failed to update CSO payment status");
    }
  };

  const processPayment = async () => {
    try {
      setLoading(true);

      const id = await createPaymentPending();
      if (!id) {
        setLoading(false);
        return;
      }

      const payload = {
        paymentId: id,
        status: "SUCCESS",
        amount: hasChangeToReturn ? amount : totalCollected,
        paymentMethod,
        branchId,
        totalBill,
        dateOfPayment: receiptDate ? receiptDate.toISOString() : null,
        denominations:
          paymentMethod === "CASH"
            ? [
              ...collectedDenominations
                .filter((d) => d.count > 0)
                .map((d) => ({ ...d, type: "CREDIT" })),
              ...changeDenominations
                .filter((d) => d.count > 0)
                .map((d) => ({ ...d, type: "DEBIT" })),
            ]
            : [],
      };

      const response = await platformApi.post("/api/payment/update", payload);

      if (response.data.code === 200) {
        message.success("Payment Successful");

        const paidAmount = Number(hasChangeToReturn ? amount : totalCollected);
        const updatedTotal = totalPaid + paidAmount;
        const newPending = Math.max(totalBill - updatedTotal, 0);

        setPartialPayments([
          ...partialPayments,
          { method: paymentMethod, amount: paidAmount },
        ]);
        setTotalPaid(updatedTotal);
        setPendingAmount(newPending);

        // Handle module-specific status updates
        if (module === "COUNTER_SALES" && paidAmount === pendingAmount) {
          try {
            const statusRes = await platformApi.put(
              `/api/saleSpareInvoice/updateStatus/${moduleId}`,
              { status: "PAID" }
            );
            if (statusRes.data.code === 200) {
              console.log("Invoice marked as PAID");
            } else {
              message.warning("Failed to update invoice status");
            }
          } catch (err) {
            console.error("Error updating invoice status:", err);
            message.error("Failed to update invoice status");
          }
        }

        // Handle CSO payment status update
        if (module === "CSO") {
          const isEligible80 = totalBill > 0 && updatedTotal >= (totalBill * 0.8);
          await updateCSOPaymentStatus(isEligible80);
        }

        if (newPending === 0) {
          message.success("Full Payment Completed");
        }

        setShowDenominationModal(false);
        onSuccess && onSuccess();
        onClose();
        window.open(`${ENDPOINT}api/payment/${getReceiptModule(module)}/receipt/${id}?group=false`, "_blank");
      } else {
        message.error("Payment failed");
      }
    } catch (err) {
      console.error("Error processing payment:", err);
      message.error("Payment failed");
    } finally {
      setLoading(false);
    }
  };


  const handleModalClose = async () => {
    console.log("handleModalClose triggered. activePlutusIdRef:", activePlutusIdRef.current, "pollingIntervalRef:", pollingIntervalRef.current);
    activePlutusIdRef.current = null;
    
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log("Cleared polling interval.");
    }

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    if (paymentId && paymentResult !== "SUCCESS") {
      try {
        await platformApi.post("/api/payment/update", {
          paymentId,
          status: "TIME_OUT",
          amount: 0,
          paymentMethod,
          branchId,
          totalBill
        });
      } catch (err) {
        console.error("Error setting TIME_OUT on modal close:", err);
      }
    }

    setLoading(false);
    onClose();
  };

  const handlePay = async (mode) => {
    setLoading(true);

    if (paymentMethod === "CASH") {
      setShowDenominationModal(true);
      setLoading(false);
    } else if (paymentMethod === "Bank Transfer") {
      const id = await createPaymentPending();
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const payload = {
          paymentId: id,
          status: "SUCCESS",
          amount,
          paymentMethod: mode,
          branchId,
          totalBill,
          dateOfPayment: receiptDate ? receiptDate.toISOString() : null,
          denominations: [],
          bank: selectedBankId,
        };


        const response = await platformApi.post("/api/payment/update", payload);

        if (response.data.code === 200) {
          message.success("Payment Successful");
          const paidAmount = Number(amount);
          const updatedTotal = totalPaid + paidAmount;
          const newPending = Math.max(totalBill - updatedTotal, 0);

          setPartialPayments([
            ...partialPayments,
            { method: paymentMethod, amount: paidAmount },
          ]);
          setTotalPaid(updatedTotal);
          setPendingAmount(newPending);

          // Handle module-specific status updates
          if (amount === pendingAmount && module === "COUNTER_SALES") {
            try {
              const statusRes = await platformApi.put(
                `/api/saleSpareInvoice/updateStatus/${moduleId}`,
                { status: "PAID" }
              );
              if (statusRes.data.code === 200) {
                console.log("Invoice marked as PAID");
              } else {
                message.warning("Failed to update invoice status");
              }
            } catch (err) {
              console.error("Error updating invoice status:", err);
              message.error("Failed to update invoice status");
            }
          }

          // Handle CSO payment status update
          if (module === "CSO") {
            const isEligible80 = totalBill > 0 && updatedTotal >= (totalBill * 0.8);
            await updateCSOPaymentStatus(isEligible80);
          }

          if (newPending === 0) {
            message.success("Full Payment Completed");
          }
          onSuccess && onSuccess();
          onClose();
          window.open(`${ENDPOINT}api/payment/${getReceiptModule(module)}/receipt/${id}?group=false`, "_blank");
        } else {
          message.error("Failed to update UPI payment");
        }
      } catch (err) {
        console.error("Error updating UPI payment:", err);
        message.error("UPI Payment failed");
      } finally {
        setLoading(false);
      }
    } else {
      const id = await createPaymentPending();
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setPaymentTimer(180);
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
        timerIntervalRef.current = setInterval(() => {
          setPaymentTimer((prev) => {
            if (prev <= 1) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        const res = await platformApi.post("/api/payment/initiateCard", {
          billAmount: totalBill,
          paymentMode: paymentMethod == "UPI" ? "10" : "1",
          paymentId: id,
          amount: amount * 100,
          branchId,
          mode: paymentMethod == "UPI" ? "UPI" : "CARD",
        });

        const plutusTransactionReferenceId =
          res?.data?.response?.pinelabsTransaction?.plutusTransactionId || res?.data?.response?.plutusTransactionReferenceId;
        
        activePlutusIdRef.current = plutusTransactionReferenceId;

        if (!plutusTransactionReferenceId) {
          let errorMessage = "No configurations available for this user";
          
          if (res?.data?.code === 200 && res?.data?.response?.pinelabsTransaction?.responsePayload) {
            try {
              const payload = JSON.parse(res.data.response.pinelabsTransaction.responsePayload);
              if (payload.ResponseMessage) {
                errorMessage = payload.ResponseMessage;
              }
            } catch (e) {
              console.error("Failed to parse responsePayload", e);
            }
          } else if (res?.data?.err) {
            errorMessage = res.data.err;
          }

          message.error(errorMessage);
          console.error("plutusTransactionReferenceId is undefined. Backend response:", res?.data);
          setLoading(false);
          return;
        }
        message.info(
          `${paymentMethod} payment initiated. Please complete the payment on the terminal.`
        );

        let attempts = 0;
        const maxAttempts = 36;
        const interval = setInterval(async () => {
          attempts++;
          try {
            const statusRes = await platformApi.get(
              `/api/payment/cardStatus/${plutusTransactionReferenceId}`
            );
            const status = statusRes?.data?.response?.status;
            if (status === "SUCCESS") {
              clearInterval(interval);
              pollingIntervalRef.current = null;
              activePlutusIdRef.current = null;
              if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
              }
              setPaymentResult("SUCCESS");
              message.success("Card Payment Successful");
              try {
                const updatePayload = {
                  paymentId: id,
                  status: "SUCCESS",
                  amount,
                  paymentMethod: "CARD",
                  branchId,
                  totalBill,
                  dateOfPayment: receiptDate ? receiptDate.toISOString() : null,
                };
                const updateRes = await platformApi.post(
                  "/api/payment/update",
                  updatePayload
                );
                if (updateRes.data.code === 200) {
                  console.log("Payment updated successfully in backend");

                  // Handle CSO payment status update for card payments
                  if (module === "CSO") {
                    const newPending = Math.max(totalBill - (totalPaid + Number(amount)), 0);
                    const isFullyPaid = newPending === 0;
                    await updateCSOPaymentStatus(isFullyPaid);
                  }
                } else {
                  message.warning("Card success but failed to update backend");
                }
              } catch (err) {
                console.error("Error updating payment after success:", err);
                message.error("Error updating payment after success");
              }

              setTimeout(() => {
                setLoading(false);
                setPaymentResult(null);
                onSuccess && onSuccess();
                onClose();
                window.open(`${ENDPOINT}api/payment/${getReceiptModule(module)}/receipt/${id}?group=false`, "_blank");
              }, 3000);
            } else if (status === "FAILED" || status === "TIME_OUT") {
              clearInterval(interval);
              pollingIntervalRef.current = null;
              activePlutusIdRef.current = null;
              if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
              }
              if (id) {
                try {
                  await platformApi.post("/api/payment/update", {
                    paymentId: id,
                    status: "TIME_OUT",
                    amount: 0,
                    paymentMethod: paymentMethod,
                    branchId,
                    totalBill
                  });
                } catch (e) {
                  console.error("Failed to mark CashFlow as TIME_OUT:", e);
                }
              }
              setPaymentResult("TIMEOUT");
              message.error("Payment Failed or Timed Out");
              setTimeout(() => {
                setLoading(false);
                setPaymentResult(null);
              }, 3000);
            } else if (attempts >= maxAttempts) {
              clearInterval(interval);
              pollingIntervalRef.current = null;
              activePlutusIdRef.current = null;
              if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
              }
              if (id) {
                try {
                  await platformApi.post("/api/payment/update", {
                    paymentId: id,
                    status: "TIME_OUT",
                    amount: 0,
                    paymentMethod: paymentMethod,
                    branchId,
                    totalBill
                  });
                } catch (e) {
                  console.error("Failed to mark CashFlow as TIME_OUT:", e);
                }
              }
              setPaymentResult("TIMEOUT");
              message.warning("Payment status check timed out!");
              setTimeout(() => {
                setLoading(false);
                setPaymentResult(null);
                onClose && onClose();
              }, 3000);
            }
          } catch (err) {
            clearInterval(interval);
            pollingIntervalRef.current = null;
            activePlutusIdRef.current = null;
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
            if (id) {
              try {
                await platformApi.post("/api/payment/update", {
                  paymentId: id,
                  status: "TIME_OUT",
                  amount: 0,
                  paymentMethod: paymentMethod,
                  branchId,
                  totalBill
                });
              } catch (e) {
                console.error("Failed to mark CashFlow as TIME_OUT on error:", e);
              }
            }
            setLoading(false);
            console.error("Error checking card payment status:", err);
            const errorMessage = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Error checking payment status";
            message.error(errorMessage);
          }
        }, 5000);
        pollingIntervalRef.current = interval;
      } catch (err) {
        console.error(err);
        message.error("Failed to initiate card payment");
      }
    }
  };

  const handleDenominationSubmit = () => {
    if (hasChangeToReturn === true) {
      if (totalChange !== autoChange) {
        message.warning(
          "Change denominations do not match auto-calculated change"
        );
        return;
      }
    } else {
      setChangeDenominations(JSON.parse(JSON.stringify(defaultDenoms)));
    }

    processPayment();
    setShowDenominationModal(false);
  };

  let availablePaymentMethods = [];
  if (
    loginCredintials.roleAccess.some(
      (acc) =>
        acc.master === "TRANSACTIONMASTER" && acc.subModule === "Cash Payment"
    )
  )
    availablePaymentMethods.push("CASH", "Bank Transfer");

  if (
    loginCredintials.roleAccess.some(
      (acc) =>
        acc.master === "TRANSACTIONMASTER" && acc.subModule === "POS Machine"
    )
  )
    availablePaymentMethods.push("CARD", "UPI");

  return (
    <>
      {loading && (paymentMethod === "CARD" || paymentMethod === "UPI") && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            zIndex: 99999,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
          }}
        >
          <style>
            {`
              .ant-message {
                z-index: 100000 !important;
              }
              @keyframes loading-blink {
                0% { opacity: 0.2; }
                20% { opacity: 1; }
                100% { opacity: 0.2; }
              }
              .loading-dots span {
                animation: loading-blink 1.4s infinite both;
              }
              .loading-dots span:nth-child(2) {
                animation-delay: 0.2s;
              }
              .loading-dots span:nth-child(3) {
                animation-delay: 0.4s;
              }
              @keyframes draw-check {
                100% { stroke-dashoffset: 0; }
              }
              @keyframes success-pop {
                0% { transform: scale(0.8); opacity: 0; }
                50% { transform: scale(1.1); opacity: 1; }
                100% { transform: scale(1); opacity: 1; }
              }
              @keyframes error-shake {
                0% { transform: translateX(0); }
                25% { transform: translateX(-10px); }
                50% { transform: translateX(10px); }
                75% { transform: translateX(-10px); }
                100% { transform: translateX(0); }
              }
              .success-animation {
                animation: success-pop 0.5s ease-out forwards;
                display: flex;
                flex-direction: column;
                align-items: center;
              }
              .error-animation {
                animation: error-shake 0.4s ease-in-out forwards;
                display: flex;
                flex-direction: column;
                align-items: center;
              }
              .draw-circle {
                stroke-dasharray: 166;
                stroke-dashoffset: 166;
                animation: draw-check 0.5s cubic-bezier(0.65, 0, 0.45, 1) forwards;
              }
              .draw-path-check {
                stroke-dasharray: 48;
                stroke-dashoffset: 48;
                animation: draw-check 0.4s cubic-bezier(0.65, 0, 0.45, 1) 0.4s forwards;
              }
              .draw-path-cross {
                stroke-dasharray: 60;
                stroke-dashoffset: 60;
                animation: draw-check 0.4s cubic-bezier(0.65, 0, 0.45, 1) 0.4s forwards;
              }
            `}
          </style>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {paymentResult === "SUCCESS" ? (
              <div className="success-animation">
                <svg viewBox="0 0 52 52" style={{ width: '100px', height: '100px', marginBottom: '20px', borderRadius: '50%', display: 'block', strokeWidth: 4, stroke: '#52c41a', strokeMiterlimit: 10, fill: 'none' }}>
                  <circle cx="26" cy="26" r="25" fill="none" className="draw-circle" />
                  <path d="M14.1 27.2l7.1 7.2 16.7-16.8" className="draw-path-check" />
                </svg>
                <h2 style={{ color: '#52c41a', marginBottom: '24px', fontSize: '32px', fontWeight: 'bold' }}>
                  Payment Successful!
                </h2>
              </div>
            ) : paymentResult === "FAILED" ? (
              <div className="error-animation">
                <svg viewBox="0 0 52 52" style={{ width: '100px', height: '100px', marginBottom: '20px', borderRadius: '50%', display: 'block', strokeWidth: 4, stroke: '#ff4d4f', strokeMiterlimit: 10, fill: 'none' }}>
                  <circle cx="26" cy="26" r="25" fill="none" className="draw-circle" />
                  <path d="M16 16 36 36 M36 16 16 36" className="draw-path-cross" />
                </svg>
                <h2 style={{ color: '#ff4d4f', marginBottom: '24px', fontSize: '32px', fontWeight: 'bold' }}>
                  Payment Failed!
                </h2>
              </div>
            ) : paymentResult === "TIMEOUT" ? (
              <div className="error-animation">
                <svg viewBox="0 0 52 52" style={{ width: '100px', height: '100px', marginBottom: '20px', borderRadius: '50%', display: 'block', strokeWidth: 4, stroke: '#faad14', strokeMiterlimit: 10, fill: 'none' }}>
                  <circle cx="26" cy="26" r="25" fill="none" className="draw-circle" />
                  <path d="M26 14 v16 M26 36 v2" style={{ strokeDasharray: 48, strokeDashoffset: 48, animation: 'draw-check 0.4s cubic-bezier(0.65, 0, 0.45, 1) 0.4s forwards' }} />
                </svg>
                <h2 style={{ color: '#faad14', marginBottom: '24px', fontSize: '32px', fontWeight: 'bold' }}>
                  Payment Timed Out!
                </h2>
              </div>
            ) : (
              <h2 style={{ color: 'white', marginBottom: '24px', fontSize: '24px' }}>
                Processing Payment <span className="loading-dots"><span>.</span><span>.</span><span>.</span></span>
              </h2>
            )}
            {!paymentResult && (
              <>
                <div style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: '24px' }}>
                  {Math.floor(paymentTimer / 60)}:{(paymentTimer % 60).toString().padStart(2, '0')}
                </div>
                {paymentTimer <= 60 && (
                  <div
                    style={{
                      background: '#fff1f0',
                      border: '1px solid #ffa39e',
                      color: '#cf1322',
                      padding: '16px 24px',
                      borderRadius: '8px',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      marginTop: '20px',
                    }}
                  >
                    DO NOT START PAYMENTS NOW !
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      <Modal
        visible={visible}
        title="Collect Payment"
        onCancel={handleModalClose}
        footer={[
          <Button key="cancel" onClick={handleModalClose}>
            Cancel
          </Button>,
          <Button
            key="pay"
            type="primary"
            loading={loading}
            onClick={() => handlePay(paymentMethod)}
            disabled={
              loading ||
              amount <= 0 ||
              ((paymentMethod === "CARD" || paymentMethod === "UPI") && amount < 1) ||
              (module !== "JobOrder" && pendingAmount > 0 && amount > pendingAmount)
            }
          >
            Collect
          </Button>,
        ]}
      >
        <Radio.Group
          value={paymentMethod}
          onChange={(e) => {
            const selectedMethod = e.target.value;
            setPaymentMethod(selectedMethod);

            if (selectedMethod !== "CASH") {
              setCollectedDenominations(
                JSON.parse(JSON.stringify(defaultDenoms))
              );
              setChangeDenominations(JSON.parse(JSON.stringify(defaultDenoms)));
            }
          }}
        >
          {availablePaymentMethods.includes("CASH") && (
            <Radio value="CASH">Cash</Radio>
          )}
          {availablePaymentMethods.includes("CARD") && (
            <Radio value="CARD">Card</Radio>
          )}
          {availablePaymentMethods.includes("UPI") && (
            <Radio value="UPI">UPI</Radio>
          )}
          {availablePaymentMethods.includes("Bank Transfer") && (
            <Radio value="Bank Transfer">Bank Transfer</Radio>
          )}
        </Radio.Group>

        <div style={{ marginTop: 16 }}>
          Amount:{" "}
          <Input
            value={amount}
            onChange={(e) => {
              const cleanedVal = e.target.value.replace(/^0+(?=\d)/, '');
              const inputAmount = Number(cleanedVal);
              // Only validate for non-jobOrder modules
              if (module !== "JobOrder") {
                // Only validate against pendingAmount once it is resolved (> 0)
                if (pendingAmount > 0 && inputAmount > pendingAmount) {
                  message.warning(`Amount cannot exceed pending amount: ₹${pendingAmount}`);
                  setAmount(pendingAmount);
                } else if (inputAmount < 0) {
                  setAmount(0);
                } else {
                  setAmount(cleanedVal);
                }
              } else {
                // For jobOrder, allow any amount
                if (inputAmount < 0) {
                  setAmount(0);
                } else {
                  setAmount(cleanedVal);
                }
              }
            }}
            type="number"
            min="0"
            // max={module !== "JobOrder" ? pendingAmount : undefined}
            addonBefore="₹"
          />
          {module !== "JobOrder" && pendingAmount > 0 && amount > pendingAmount && (
            <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
              Amount exceeds pending balance of ₹{pendingAmount}
            </div>
          )}
          {(paymentMethod === "CARD" || paymentMethod === "UPI") && amount > 0 && amount < 1 && (
            <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
              Amount must be at least ₹1 for {paymentMethod} transactions
            </div>
          )}
        </div>

        {paymentMethod === "Bank Transfer" && (
          <div style={{ marginTop: 16 }}>
            <strong>Select Bank:</strong>
            <Select
              showSearch
              placeholder="Select Bank Account"
              optionFilterProp="children"
              value={selectedBankId}
              onChange={(value) => setSelectedBankId(value)}
              style={{ width: "100%", marginTop: 8 }}
            >
              {branchBanks.map((bank) => (
                <Select.Option key={bank.id} value={bank.id}>
                  {bank.accountNumber} - {bank.branchName}
                </Select.Option>
              ))}
            </Select>
          </div>
        )}

        {(paymentMethod === "Bank Transfer" || paymentMethod === "CASH") && (
          <div style={{ marginTop: 16 }}>
            <strong>Receipt Date:</strong>
            <DatePicker
              style={{ width: "100%", marginTop: 8 }}
              format="DD/MM/YYYY"
              value={receiptDate}
              onChange={(date) => setReceiptDate(date)}
              disabledDate={(current) => {
                if (!current) return false;
                if (current > moment().endOf("day")) {
                  return true;
                }
                const allowPastDate = loginCredintials?.department?.allowPastDate;
                const pastDateLimit = loginCredintials?.department?.pastDateLimit;
                if (allowPastDate) {
                  if (pastDateLimit !== undefined && pastDateLimit !== null) {
                    const limitDate = moment().startOf("day").subtract(pastDateLimit, "days");
                    return current < limitDate;
                  }
                  return false;
                } else {
                  return current < moment().startOf("day");
                }
              }}
            />
          </div>
        )}


        <div
          style={{ marginTop: 20, borderTop: "1px solid #eee", paddingTop: 10 }}
        >
          <div>
            <strong>Amount to be collected:</strong> ₹{totalBill}
          </div>
          <div>
            <strong>Total Collected:</strong> ₹{totalPaid}
          </div>
          <div>
            <strong>Pending:</strong> ₹{pendingAmount}
          </div>
        </div>
      </Modal>

      {/* Denomination Modal */}
      <Modal
        visible={showDenominationModal}
        title={step === 1 ? "Enter Collected Cash" : "Enter Change Returned"}
        onCancel={() => {
          setStep(1);
          setShowDenominationModal(false);
        }}
        footer={[
          step === 2 && (
            <Button key="back" onClick={() => setStep(1)}>
              Back
            </Button>
          ),
          step === 1 && (
            <Button
              key="next"
              type="primary"
              onClick={() => {
                if (totalCollected < amount) {
                  message.error(
                    "Collected amount cannot be less than bill amount"
                  );
                  return;
                }
                const autoChangeAmount = Math.max(totalCollected - amount, 0);
                if (autoChangeAmount === 0) {
                  processPayment();
                  setShowDenominationModal(false);
                } else {
                  setStep(2);
                }
              }}
            >
              {Math.max(totalCollected - amount, 0) === 0 ? "Submit" : "Next"}
            </Button>
          ),
          step === 2 && (
            <Button
              key="submit"
              type="primary"
              onClick={handleDenominationSubmit}
              loading={loading}
            >
              Submit
            </Button>
          ),
        ]}
      >
        {step === 1 ? (
          <>
            <h4>
              Amount to be collected:{" "}
              <span
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  padding: "2px 6px",
                  background: "#f9f9f9",
                  display: "inline-block",
                  minWidth: "40px",
                  textAlign: "center",
                }}
              >
                {amount}
              </span>
            </h4>

            <h4>Collected from Customer</h4>
            {collectedDenominations.map((d, index) => (
              <div
                key={d.denomination}
                style={{
                  marginBottom: 8,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ width: 80 }}>{d.denomination} :</span>
                <Input
                  type="number"
                  min="0"
                  value={d.count === 0 ? "" : d.count}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^[0-9]+$/.test(val)) {
                      const newDenoms = [...collectedDenominations];
                      newDenoms[index].count = val === "" ? "" : parseInt(val);
                      setCollectedDenominations(newDenoms);
                    }
                  }}
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => {
                    if (["-", "e", "+", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  style={{ width: 80, marginRight: 16 }}
                />

                <span>Total: {d.denomination * (d.count || 0)}</span>
              </div>
            ))}

            <div style={{ marginTop: 16 }}>
              <strong>Total Collected: </strong> {totalCollected}
            </div>

            <div style={{ marginTop: 16 }}>
              <strong>Change to be returned: </strong>{" "}
              {Math.max(totalCollected - amount, 0)}
            </div>
          </>
        ) : (
          <>
            <h4>Change Returned to Customer</h4>

            {hasChangeToReturn && (
              <>
                {changeDenominations.map((d, index) => (
                  <div
                    key={d.denomination}
                    style={{
                      marginBottom: 8,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ width: 80 }}>{d.denomination} :</span>
                    <Input
                      type="number"
                      min="0"
                      value={d.count === 0 ? "" : d.count}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^[0-9]+$/.test(val)) {
                          const newDenoms = [...changeDenominations];
                          newDenoms[index].count =
                            val === "" ? "" : parseInt(val);
                          setChangeDenominations(newDenoms);
                        }
                      }}
                      style={{ width: 80, marginRight: 16 }}
                    />
                    <span>Total: {d.denomination * (d.count || 0)}</span>
                  </div>
                ))}
              </>
            )}

            <div style={{ marginBottom: 16 }}>
              <strong>Do we have change to return?</strong>
              <Radio.Group
                value={hasChangeToReturn}
                onChange={(e) => setHasChangeToReturn(e.target.value)}
                style={{ marginLeft: 16 }}
              >
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </div>

            <div style={{ marginTop: 16 }}>
              <strong>Total Change Given: </strong> {totalChange}
            </div>
            {hasChangeToReturn ? (
              <div style={{ marginTop: 16 }}>
                <strong>Expected Change: </strong> {autoChange}
              </div>
            ) : (
              <div>No change</div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};
export default PaymentModal;
